import React, { useRef, useEffect, useState } from "react";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import { ChevronLeft, ChevronRight, Activity, Users, Building, Globe } from "lucide-react";
import about_1 from '../assets/images/DJI_0838.jpg'
export default function About() {
  const containerRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const timelineData = [
    {
      year: "Inspired by Our Vision",
      description: "To become the most preferred and trusted manufacturing and solutions partner for brands operating across diverse verticals. We aspire to lead with innovation, delivering exceptional quality and reliability, while empowering businesses to achieve their goals. By continuously adapting to market dynamics and exceeding expectations, we aim to be a cornerstone of success for our partners globally.",
    },
    {
      year: "Driven by Our Mission",
      description: "Customer First – Strengthen customer partnerships by providing products and services of the greatest value through innovation and excellence.",
    },
    {
      year: "Joined by Our Values",
      description: "We expanded our services to include checking and savings accounts with no monthly fees or minimum balance requirements.",
    },
  ];
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"]
  });

  const y = useTransform(scrollYProgress, [0, 1], [0, -300]);
  const scale = useTransform(scrollYProgress, [0, 0.5], [1, 1.2]);
  
  const springConfig = { stiffness: 100, damping: 15 };
  const scaleSpring = useSpring(1, springConfig);

  const checkScrollButtons = () => {
    const el = scrollContainerRef.current;
    if (el) {
      setCanScrollLeft(el.scrollLeft > 0);
      setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth);
    }
  };

  const scroll = (direction) => {
    const el = scrollContainerRef.current;
    if (el) {
      const scrollAmount = 400;
      el.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  useEffect(() => {
    const el = scrollContainerRef.current;
    if (el) {
      el.addEventListener('scroll', checkScrollButtons);
      checkScrollButtons();
      return () => el.removeEventListener('scroll', checkScrollButtons);
    }
  }, []);

  return (
    <div ref={containerRef} className="relative bg-white text-gray-900 overflow-hidden">
      {/* Hero Section with Parallax */}
      <div className="relative h-[70vh] flex items-center justify-center">
        <motion.div 
          style={{ y }}
          className="absolute inset-0 overflow-hidden"
        >
          <div className="absolute inset-0 bg-gradient-to-b from-orange-500/10 to-white/80 mix-blend-multiply" />
          <img
            src={"/placeholder.svg"}
            alt="Hero Background"
            className="w-full h-full object-cover scale-110"
          />
        </motion.div>
        
        <div className="relative z-10 text-center px-4">
          <motion.div
            initial={{ opacity: 0, y: 100 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.5, ease: "easeOut" }}
          >
            <h1 className="text-6xl md:text-9xl font-bold tracking-tighter mb-6">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-yellow-500">
                Our Legacy
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-700 max-w-2xl mx-auto">
              Building tomorrow's technology with today's innovation
            </p>
          </motion.div>
        </div>
      </div>

     
      {/* Mission Statement - Enhanced Layout */}
      <section className="relative py-12">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -100 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 1 }}
            >
              <h2 className="text-4xl md:text-5xl font-bold mb-8 leading-tight">
                Driving Innovation in
                <span className="text-orange-600"> Manufacturing</span>
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed mb-8">
                Since 2002, Bhagwati Products Limited has been at the forefront of 
                manufacturing excellence in India. From our humble beginnings in New Delhi, 
                we've grown to become a leader in telecommunication devices and consumer electronics.
              </p>
              <div className="grid grid-cols-2 gap-8">
                <div className="bg-orange-50 p-6 rounded-lg">
                  <h3 className="font-bold text-2xl text-orange-600 mb-2">20+</h3>
                  <p className="text-gray-600">Years of Excellence</p>
                </div>
                <div className="bg-orange-50 p-6 rounded-lg">
                  <h3 className="font-bold text-2xl text-orange-600 mb-2">100+</h3>
                  <p className="text-gray-600">Global Patents</p>
                </div>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1 }}
              className="relative"
            >
              <div className="aspect-square rounded-2xl overflow-hidden">
                <img
                  src={about_1}
                  alt="Manufacturing Excellence"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Team Section - Staggered Grid
      <section className="py-32 relative bg-gray-50" >
        <div className="container mx-auto px-4" id="leadership">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            className="text-center mb-16"
          >
            <h2 className="text-5xl font-bold mb-4 text-gray-900">Our Leadership</h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12">
            {[
              { name: "Leadership Team", role: "Management", image: "/placeholder.svg" },
              { name: "Innovation Team", role: "R&D", image: "/placeholder.svg" },
              { name: "Manufacturing Team", role: "Operations", image: "/placeholder.svg" }
            ].map((member, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                whileHover={{ y: -10 }}
                transition={{ duration: 0.5, delay: index * 0.2 }}
                className="relative group"
              >
                <div className="aspect-square overflow-hidden rounded-xl bg-white shadow-lg">
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent z-10" />
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute bottom-0 left-0 right-0 p-8 z-20">
                    <h3 className="text-2xl font-bold mb-2 text-white">{member.name}</h3>
                    <p className="text-orange-300">{member.role}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section> */}
 {/* New Timeline Section */}
 {/* <section className="py-24">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          className="text-center mb-16"
        >
          <h2 className="text-5xl font-bold mb-4">Our Journey</h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Two decades of innovation and growth in manufacturing excellence
          </p>
        </motion.div>

        <div className="relative">
          // Center line 
          <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 h-full w-0.5 bg-orange-100" />
          
         // Mobile line
          <div className="absolute left-5 md:hidden h-full w-0.5 bg-orange-100" />

          <div className="space-y-12">
            {[
              {
                year: "2012-13",
                title: "The Foundation Year",
                description: "Started production of LED TV, Tablet PC at Rudrapur Plant",
                
              },
              {
                year: "2013-14",
                title: "Diversification Era",
                description: "Started production of Smartphones, Tablet PCs.",
                
              },
              {
                year: "2014-15",
                title: "Global Outreach",
                description: "Started production of backward integration of LED TV, and 3G/4G Internet dongle Speakers & Sound Bars",
                
              },
              {
                year: "2015-16",
                title: "Hyderabad Milestone",
                description: "New Factory at Hyderabad",
                
              },
              {
                year: "2016-17",
                title: "Scaling New Heights",
                description: "New Factory at Bhiwadi",
                
              },
              {
                year: "2017-18",
                title: "Automation Boost",
                description: "Addition of high-speed SMT and wave soldering lines",
                
              },
              {
                year: "2018-20",
                title: "Revolutionary Integration",
                description: "Moulding machine of capacity 160/250/650T in phase -1",
                
              },
              {
                year: "2020-23",
                title: "Market Leadership",
                description: "Air-conditioner, Press shop, HE & Paint shop, TWS & Smartwatch",
                
              },
              {
                year: "2024",
                title: "Future Forward",
                description: "New Factory at Greater Noida",
                
              },
            ].map((milestone, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.2 }}
                className="relative flex md:justify-center items-center"
              >
                <div className={`flex items-center ${
                  index % 2 === 0 
                    ? 'md:flex-row-reverse md:w-1/2 md:ml-auto md:pr-8' 
                    : 'md:w-1/2 md:mr-auto md:pl-8'
                }`}>
                  //Circle marker 
                  <div className="w-10 h-10 rounded-full border-4 border-orange-50 bg-orange-600 absolute left-0 md:static 
                    md:absolute md:left-1/2 md:transform md:-translate-x-1/2" />
                  
                  // Content card 
                  <div className={`bg-white p-6 md:p-8 rounded-lg shadow-lg ml-20 md:ml-0 md:w-full ${
                    index % 2 === 0 
                      ? 'md:mr-12' 
                      : 'md:ml-12'
                  }`}>
                    <span className="text-orange-600 font-bold text-xl">{milestone.year}</span>
                    <h3 className="text-2xl font-bold mt-2">{milestone.title}</h3>
                    <p className="text-gray-600 mt-2">{milestone.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section> */}
    
      {/* Stats Section - Animated Counter
      <section className="py-32 relative overflow-hidden bg-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-16">
            {[
              { number: "300+", label: "Team Members" },
              { number: "40M+", label: "Mobile Phones Produced" },
              { number: "7M+", label: "LED TVs Manufactured" }
            ].map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ delay: index * 0.2 }}
                className="text-center relative group cursor-pointer"
                onHoverStart={() => scaleSpring.set(1.1)}
                onHoverEnd={() => scaleSpring.set(1)}
              >
                <motion.div style={{ scale: scaleSpring }}>
                  <h3 className="text-7xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-orange-600 to-yellow-500">
                    {stat.number}
                  </h3>
                  <p className="text-xl text-gray-600">{stat.label}</p>
                </motion.div>
              </motion.div>
            ))}
          </div>
        </div>
      </section> */}
    </div>
  );
}